import { L, cls } from "./shared";
import { ChevronDown } from "lucide-react";
import { enterToNext } from "../../lib/focusNext";

export const BRANCH_TYPES = ["GENEL MÜDÜRLÜK", "ŞUBE", "İŞTİRAK", "ATM", "DÜZELTME"];
export const isAtm = (l) => (l?.branch_type || "ŞUBE") === "ATM";
export const isCorrection = (l) => !!l?.is_correction || l?.branch_type === "DÜZELTME";   // düzeltme farkları şubesi (tür DÜZELTME)
const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(String(v).replace(",", ".")));
const areaNum = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;                       // "12,5" → 12.5
// Şube m² dağılımı — alan kutuları yan yana (ATM'de gösterilmez); backend `area_m2` sözlüğünde saklanır. Tadilat temizliği m² = Şube m² (ayrı kutu yok)
export const AREA_FIELDS = [
  ["rezerv", "Rezerv m²"], ["mescit", "Mescit"], ["mutfak", "Mutfak"], ["arsiv", "Arşiv"], ["kasa_dairesi", "Kasa Dairesi"],
  ["jenerator", "Jeneratör"], ["mekanik_oda", "Mekanik Oda"], ["elektrik_odasi", "Elektrik Odası"], ["ilave_oda", "İlave Oda"], ["ilave_alan_1", "İlave Alan 1"], ["ilave_alan_2", "İlave Alan 2"], ["genel_temizlik", "Genel Temizlik m²"],
];

export function BranchTypeField({ form, set, readOnly }) {
  const corr = isCorrection(form);
  const options = form.is_correction ? ["DÜZELTME"] : BRANCH_TYPES.filter((t) => t !== "DÜZELTME");   // DÜZELTME elle seçilemez (bölge açılınca otomatik)
  return (
    <L label="Şube Türü">
      <select disabled={readOnly || !!form.is_correction} data-testid="loc-field-type" value={form.branch_type || "ŞUBE"} onChange={(e) => set({ branch_type: e.target.value })} title={form.is_correction ? "Otomatik düzeltme farkları şubesi — türü değiştirilemez" : undefined} className={`${cls} disabled:bg-slate-50 disabled:text-slate-700 ${isAtm(form) ? "border-sky-400 bg-sky-50 font-semibold" : corr ? "border-violet-400 bg-violet-50 font-semibold text-violet-900" : ""}`}>
        {options.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
    </L>
  );
}

// ATM: m² yerine Enlem / Boylam / Aylık Silim Sayısı
export function AtmFields({ form, set, readOnly }) {
  return (
    <>
      <L label="Enlem (Lat)"><input readOnly={readOnly} type="number" step="any" min="-90" max="90" data-testid="loc-field-lat" value={form.latitude ?? ""} onChange={(e) => set({ latitude: num(e.target.value) })} className={`${cls} font-mono`} placeholder="örn. 40.9797" /></L>
      <L label="Boylam (Lng)"><input readOnly={readOnly} type="number" step="any" min="-180" max="180" data-testid="loc-field-lng" value={form.longitude ?? ""} onChange={(e) => set({ longitude: num(e.target.value) })} className={`${cls} font-mono`} placeholder="örn. 28.7214" /></L>
      <L label="Aylık Silim Sayısı">
        <input readOnly={readOnly} type="number" min="0" step="1" data-testid="loc-field-wipes" value={form.monthly_wipe_count ?? 0} onChange={(e) => set({ monthly_wipe_count: e.target.value === "" ? 0 : parseFloat(e.target.value) })} className={cls} placeholder="örn. 30" />
        <div className="text-[10px] text-slate-500 mt-1">Veri girişinde bu ATM seçilince Miktar otomatik bu sayı ile dolar (değiştirilebilir).</div>
      </L>
    </>
  );
}

export const areaTotal = (area) => Math.round(AREA_FIELDS.reduce((s, [k]) => s + areaNum((area || {})[k]), 0) * 100) / 100;

// Şube m²: dağılım kutularında değer varsa otomatik toplam (salt okunur), yoksa elle girilir. Yanında "m² Dağılımı" aç/kapa düğmesi (açık gelir)
export function M2Field({ form, set, readOnly, areasOpen, onToggleAreas }) {
  const total = areaTotal(form.area_m2);
  const auto = total > 0;
  const filled = AREA_FIELDS.filter(([k]) => areaNum((form.area_m2 || {})[k]) > 0).length;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Şube m²{auto && <span className="ml-1 text-emerald-700" data-testid="loc-m2-auto">· dağılım toplamı</span>}</label>
        {onToggleAreas && (
          <button type="button" onClick={onToggleAreas} aria-expanded={areasOpen} data-testid="loc-areas-toggle" className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-800 hover:text-amber-950">
            <ChevronDown className={`w-3 h-3 transition-transform ${areasOpen ? "rotate-180" : ""}`} /> m² Dağılımı{filled ? ` (${filled})` : ""}
          </button>
        )}
      </div>
      <input readOnly={readOnly || auto} type="number" min="0" step="any" data-testid="loc-field-m2" value={auto ? total : (form.branch_m2 ?? 0)} onChange={(e) => set({ branch_m2: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
        className={`${cls} ${auto ? "!bg-emerald-50 font-semibold" : ""}`} title={auto ? "m² dağılımındaki kutuların toplamı — kutuları değiştirince güncellenir" : ""} placeholder="örn. 640" />
    </div>
  );
}

const fmtM2 = (n) => `${(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} m²`;
const areaText = (v) => (v === 0 || v === "" || v == null ? "" : String(v).replace(".", ","));      // 0 → boş hücre; 12.5 → "12,5"
const cleanArea = (s) => { const t = s.replace(/[^\d.,]/g, "").replace(".", ","); const i = t.indexOf(","); return i === -1 ? t : t.slice(0, i + 1) + t.slice(i + 1).replace(/,/g, "").slice(0, 2); };   // yalnız rakam + tek ondalık virgül, eksi yok
const intOrNull = (s) => { const d = s.replace(/\D/g, ""); return d === "" ? null : parseInt(d, 10); };   // kat sayısı: yalnız tam sayı, boş → null

// Kat bilgisi (m² toplamına girmez): toplam kat / kullanılan kat — kullanılan toplamı aşarsa uyarı
export const FLOOR_FIELDS = [["floor_count_total", "Toplam Kat Sayısı"], ["floor_count_used", "Kullanılan Kat Sayısı"]];
function FloorFields({ form, set, readOnly }) {
  const t = form.floor_count_total, u = form.floor_count_used;
  const over = t != null && u != null && u > t;
  return (
    <div className="flex flex-wrap items-end gap-2 mt-3 pt-3 border-t border-dashed border-slate-200" data-testid="loc-floor-fields">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 self-center mr-1">Kat Bilgisi</span>
      {FLOOR_FIELDS.map(([k, label]) => (
        <div key={k} className="w-40">
          <L label={label}>
            <input readOnly={readOnly} type="text" inputMode="numeric" autoComplete="off" data-testid={`loc-field-${k.replace(/_/g, "-")}`} value={form[k] ?? ""} onFocus={(e) => e.target.select()}
              onChange={(e) => set({ [k]: intOrNull(e.target.value) })} className={`${cls} text-right ${form[k] != null ? "font-semibold" : ""} ${over && k === "floor_count_used" ? "!border-red-400 !bg-red-50" : ""}`} placeholder="örn. 5" />
          </L>
        </div>
      ))}
      <span className="text-[10px] text-slate-500 pb-2" data-testid="loc-floor-hint">{over ? <b className="text-red-600">Kullanılan kat toplam katı aşamaz</b> : t != null || u != null ? `${u ?? "—"} / ${t ?? "—"} kat kullanımda` : "m² toplamına girmez"}</span>
    </div>
  );
}

// Enter → bir sonraki kutuya geç (son kutudan sonra Kaydet düğmesine); ortak uygulama lib/focusNext'te
export { enterToNext };

export function AreaFields({ form, set, readOnly }) {
  const area = form.area_m2 || {};
  const total = areaTotal(area);
  const setArea = (k, v) => { const next = { ...area, [k]: v }; const t = areaTotal(next); set({ area_m2: next, branch_m2: t > 0 ? t : form.branch_m2 }); };   // Şube m² = kutuların toplamı
  return (
    <div className="col-span-full border-t border-slate-100 pt-3" data-testid="loc-area-fields">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">m² Dağılımı</span>
        <span className="text-[10px] text-slate-500" data-testid="loc-area-total">Toplam: <b>{fmtM2(total)}</b>{total > 0 ? <> → <b className="text-emerald-700">Şube m²</b> otomatik bu toplamdır</> : <> · kutular boşsa Şube m² elle girilir</>}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 xl:grid-cols-12 gap-2">
        {AREA_FIELDS.map(([k, label]) => (
          <div key={k} className="flex flex-col justify-end min-w-0">
            <L label={label}>
              <input readOnly={readOnly} type="text" inputMode="decimal" autoComplete="off" data-testid={`loc-area-${k}`} value={areaText(area[k])} onFocus={(e) => e.target.select()}
                onChange={(e) => setArea(k, cleanArea(e.target.value))} className={`${cls} text-right ${areaNum(area[k]) ? "font-semibold" : ""}`} />
            </L>
          </div>
        ))}
      </div>
      <FloorFields form={form} set={set} readOnly={readOnly} />
    </div>
  );
}
